<template>
  <v-form ref="form" v-model="valid">
    <v-card class="elevation-12">
      <v-toolbar dark color="primary">
        <v-toolbar-title>{{ $t("loginForm.authorization") }}</v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-card-text>
        <LoadIcon v-if="isLogged" />

        <label>{{ this.$t('loginForm.login') }}</label>
        <v-text-field
          prepend-icon="mdi-account"
          name="login"

          type="text"
          v-model="login"
          :rules="loginRules"
          @keydown="inputHandler"
        />
        <label>{{ this.$t('loginForm.password') }}</label>
        <v-text-field
          prepend-icon="mdi-lock"
          name="password"

          id="password"
          :type="showPassword ? 'text' : 'password'"
          v-model="password"
          :rules="passwordRules"
          @keydown="inputHandler"
          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          @click:append="showPassword = !showPassword"
        />
      </v-card-text>
      <v-card-actions>
        <p v-if="tryCount !== -1" class="ml-3" style="color:red;">
          {{ $t("loginForm.tryCount") }}: {{ tryCount }}
        </p>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!valid || isLogged"
          @click="() => onNewLogin(false)"
        >
          {{ $t("loginForm.logButton") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { notifyError, notifyWarning } from '@/services/notifications';
import { makeBasicAuthToken } from '@/services/auth';
import LoadIcon from '@/components/LoadIcon';
import { mapGetters } from 'vuex';

export default {
  name: 'LoginForm',
  components: {
    LoadIcon,
  },
  data() {
    return {
      login: '',
      isLogged: false,
      password: '',
      valid: false,
      tryCount: -1,
      permissibleChars: /^[A-Za-z0-9+!#$@\-_.]+$/,
      showPassword: false,
    };
  },
  computed: {
    loginRules() {
      return [(v) => !!v || this.$t('loginForm.loginIsRequired')];
    },
    passwordRules() {
      return [
        (v) => !!v || this.$t('loginForm.passwordIsRequired'),
        (v) =>
          v.match(this.permissibleChars) !== null ||
          this.$t('resetPasswordForm.unaccepttableSymb'),
      ];
    },
    ...mapGetters(['isLoggedIn']),
  },
  async mounted() {
    this.tryCount = -1;
  },
  methods: {
    inputHandler(e) {
      if (e.keyCode === 13 && !e.shiftKey) {
        e.preventDefault();
        if (this.login && this.password) this.onNewLogin(false);
      }
    },
    async onNewLogin() {
      if (this.isLoggedIn) return;
      this.isLogged = true;
      
      try {
        await this.$store.dispatch('login', {
          data: {
            login: this.login,
            password: this.password,
          },
          vm: this,
        });
        
        let path = location.href;
        path = path.replace('login', 'cards');
        location.replace(path);
      } catch (err) {
        console.error(err);

        if (!err.response) {
          notifyError('', err);
          this.isLogged = false;
          
          return;
        }

        const { errorType, identifier, count } = err.response.data;
        switch (errorType) {
        case 'NEED_RESET_PASSWORD': {
          notifyWarning('', this.$t('loginForm.needResetPassword'));
          this.$store.commit('set_token', identifier);
          this.$router.push({
            path: '/login',
            query: { mode: 'reset', forced: true, login: this.login },
          });
          break;
        }
        case 'WRONG_PASSWORD':
        case 'WRONG_LOGIN': {
          notifyWarning('', this.$t('loginForm.wrongPassword'));
          if (count !== undefined && count !== null) this.tryCount = count;
          else this.tryCount = -1;
          break;
        }
        case 'NO_RIGHTS': {
          notifyError('', this.$t('loginForm.noRights'));
          this.$router.push({
            path: '/login',
          });
          break;
        }
        case 'BLOCKED': {
          notifyError('', this.$t('loginForm.blocked'));
          break;
        }
        default: {
          notifyError(err, this.$t('Error'));
          break;
        }
        }

        this.isLogged = false;
      }
    },
    loginIn({ commit }, { data, vm }) {
      return new Promise((resolve, reject) => {
        commit('auth_request');

        const { login, password } = data;
        const token = makeBasicAuthToken(login, password);

        commit('set_token', token);

        vm.$axios.login
          .getIdentifier()
          .then((response) => {
            const { data } = response;
            const { identifier } = data;
            commit('set_token', identifier);
            sessionStorage.setItem('token', identifier);

            return response;
          })
          .then(async () => {
            await vm.$axios.login.getUserData().then((response) => {
              const { rights, name } = response.data;
              commit('auth_success', { rights, name });
              resolve(response);
            });
          })
          .catch((err) => {
            commit('auth_error');
            commit('remove_token');
            sessionStorage.removeItem('token');
            reject(err);
          });
      });
    },
  },
};
</script>

